'use client';

import { useState } from 'react';
import { SOURCES } from '@/data/sources';
import { useCalendar } from '@/data/sources/calendar';
import { useFlights } from '@/data/sources/flights';
import { useGeo } from '@/data/sources/geo';
import { useLocation } from '@/data/sources/location';
import { useVitals } from '@/data/sources/vitals';

const ago = (d: Date | null) =>
  d
    ? `${d.toLocaleTimeString()} · ${Math.round((Date.now() - d.getTime()) / 1000)}s ago`
    : '—';

const preview = (data: unknown) => {
  if (data == null) return 'null';
  if (Array.isArray(data)) return `${data.length} items`;
  return `{ ${Object.keys(data as object).join(', ')} }`;
};

const mono = 'var(--font-supply-mono, monospace)';

export const DataDebug = () => {
  const [open, setOpen] = useState(false);

  const resources = {
    flights: useFlights(),
    vitals: useVitals(),
    location: useLocation(),
    geo: useGeo(),
    calendar: useCalendar(),
  };

  const liveIds = new Set(Object.keys(resources));
  const otherSources = Object.values(SOURCES).filter((s) => !liveIds.has(s.id));

  return (
    <div
      style={{
        position: 'fixed',
        left: 12,
        bottom: 12,
        zIndex: 9999,
        fontFamily: mono,
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          fontSize: 10,
          padding: '4px 9px',
          background: '#111',
          color: '#5f5',
          border: '1px solid #333',
          borderRadius: 4,
          cursor: 'pointer',
          letterSpacing: '0.1em',
        }}
      >
        {open ? 'DATA ×' : 'DATA'}
      </button>

      {open && (
        <div
          style={{
            marginTop: 8,
            width: 'min(440px, 92vw)',
            maxHeight: '72vh',
            overflow: 'auto',
            background: 'rgba(0,0,0,0.92)',
            color: '#ddd',
            border: '1px solid #333',
            borderRadius: 6,
            padding: 12,
            fontSize: 11,
            lineHeight: 1.55,
          }}
        >
          {Object.entries(resources).map(([name, r]) => {
            const cfg = SOURCES[name as keyof typeof SOURCES];

            return (
              <div
                key={name}
                style={{
                  marginBottom: 10,
                  paddingBottom: 10,
                  borderBottom: '1px solid #222',
                }}
              >
                <div style={{ color: '#fff', fontWeight: 700 }}>
                  {name} <span style={{ color: '#888' }}>· {cfg.kind}</span>
                  {r.isLoading && (
                    <span style={{ color: '#fb0' }}> · loading</span>
                  )}
                  {r.stale && <span style={{ color: '#f66' }}> · stale</span>}
                  {r.error ? (
                    <span style={{ color: '#f33' }}> · error</span>
                  ) : null}
                </div>
                <div style={{ color: '#9cf' }}>source: {r.source ?? '—'}</div>
                <div>freshAt: {ago(r.freshAt)}</div>
                <div>fetchedAt: {ago(r.fetchedAt)}</div>
                <div>data: {preview(r.data)}</div>
                <details>
                  <summary style={{ cursor: 'pointer', color: '#777' }}>
                    raw
                  </summary>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      color: '#aaa',
                      margin: '4px 0 0',
                    }}
                  >
                    {JSON.stringify(r.data, null, 2)}
                  </pre>
                </details>
              </div>
            );
          })}

          <div style={{ color: '#666' }}>
            also registered (no live hook):{' '}
            {otherSources.map((s) => `${s.id} (${s.kind})`).join(', ')}
          </div>
        </div>
      )}
    </div>
  );
};
